<script setup lang="ts">
/**
 * Skeleton para grid de cards MiniGames (tabs específicas, não "Todos")
 * Cards verticais: 200x217 mobile / 215x217 desktop (igual MiniGame.vue)
 * Uso: <SmarticoApostatudoSkeletonGridSkeleton :count="8" :height="220" />
 */

interface Props {
  /** Quantidade de items skeleton */
  count?: number;
  /** Altura de cada item em pixels */
  height?: number;
  /** Classe CSS customizada para o grid */
  gridClass?: string;
}

withDefaults(defineProps<Props>(), {
  count: 8,
  height: 217,
  gridClass: '',
});
</script>

<template>
  <div :class="['gridItems', gridClass]">
    <div
      v-for="n in count"
      :key="`skeleton-${n}`"
      class="skeleton-card"
      :style="{ height: `${height}px` }"
    >
      <!-- Imagem quadrada no topo -->
      <div class="skeleton-card-image" />
      <!-- Título -->
      <div class="skeleton-card-title" />
      <!-- Descrição -->
      <div class="skeleton-card-desc" />
      <!-- Ações: Preço + Botão -->
      <div class="skeleton-card-actions">
        <div class="skeleton-card-price" />
        <div class="skeleton-card-button" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.gridItems {
  @apply grid gap-3 overflow-visible;
  grid-template-columns: repeat(auto-fill, minmax(208px, 1fr));
  padding-top: 12px;
  justify-items: center;

  @media (max-width: 639px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  @media (max-width: 450px) {
    gap: 6px;
  }
}

/* Card vertical - igual MiniGame.vue */
.skeleton-card {
  @apply flex flex-col items-center;
  @apply rounded-[13px];
  /* Mobile: 200px width */
  width: 200px;
  padding: 13px;
  gap: 6px;
  background: #010612;
  border: 1px solid #1d2329;
}

/* Desktop: 215px width */
@media (min-width: 640px) {
  .skeleton-card {
    width: 215px;
  }
}

/* Telas pequenas */
@media (max-width: 450px) {
  .skeleton-card {
    width: 100%;
    max-width: 180px;
    min-height: 180px;
    padding: 10px;
    gap: 5px;
  }

  .skeleton-card-image {
    width: 65px !important;
    height: 65px !important;
  }

  .skeleton-card-title {
    height: 15px !important;
  }

  .skeleton-card-desc {
    height: 14px !important;
  }

  .skeleton-card-actions {
    height: 30px !important;
  }
}

/* Imagem quadrada no topo (96x96) */
.skeleton-card-image {
  @apply flex-shrink-0 rounded-lg;
  width: 96px;
  height: 96px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

/* Título */
.skeleton-card-title {
  @apply self-stretch rounded;
  height: 16px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

/* Descrição */
.skeleton-card-desc {
  @apply self-stretch rounded;
  height: 32px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

/* Ações: Preço + Botão */
.skeleton-card-actions {
  @apply self-stretch flex justify-center items-stretch;
  gap: 6px;
  height: 36px;
}

/* Badge de Preço */
.skeleton-card-price {
  @apply flex-1 rounded-lg;
  background: rgba(0, 201, 255, 0.1);
}

/* Botão */
.skeleton-card-button {
  @apply flex-1 rounded;
  background: linear-gradient(
    90deg,
    rgba(255, 112, 74, 0.3) 25%,
    rgba(255, 52, 0, 0.4) 50%,
    rgba(255, 112, 74, 0.3) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
